import { site } from '../data/site.js';
import { getLanguage } from './i18n.js';
import { DEFAULT_LOCALE, LOCALES } from '../i18n/locales.js';

/**
 * URL grammar: the default locale lives at the root, every other locale gets
 * one lowercase prefix segment. Routes are trailing-slash directories so any
 * static file server can resolve `/games/` to `games/index.html` without a
 * rewrite rule.
 */
const SLUG_BY_CODE = new Map(LOCALES.map((locale) => [locale.code, locale.slug]));
const CODE_BY_SLUG = new Map(
  LOCALES.filter((locale) => locale.code !== DEFAULT_LOCALE).map((locale) => [locale.slug, locale.code]),
);

export const normalizeRoutePath = (value) => {
  if (typeof value !== 'string' || !value || value === '/') return '/';
  const trimmed = value.replace(/^\/+/, '').replace(/\/+$/, '');
  return trimmed ? `/${trimmed}` : '/';
};

export const routeHref = (path = '/', code = getLanguage()) => {
  const route = normalizeRoutePath(path);
  const prefix = code === DEFAULT_LOCALE ? '' : `/${SLUG_BY_CODE.get(code) ?? code.toLowerCase()}`;
  return route === '/' ? `${prefix}/` : `${prefix}/${route.slice(1)}/`;
};

export const absoluteUrl = (path = '/', code = getLanguage()) => `${site.url}${routeHref(path, code)}`;

/** Every language version of a route, plus the x-default target. */
export const alternatesFor = (path) => [
  ...LOCALES.map((locale) => ({ hreflang: locale.htmlLang, href: absoluteUrl(path, locale.code) })),
  { hreflang: 'x-default', href: absoluteUrl(path, DEFAULT_LOCALE) },
];

/** Turn a pathname into its locale-independent route path. */
export const parsePath = (pathname = '/') => {
  let value = pathname;
  try {
    value = decodeURI(pathname);
  } catch {
    /* keep the raw value; unknown paths fall through to the 404 view */
  }

  value = value.replace(/\/index\.html$/i, '/');
  const segments = value.split('/').filter(Boolean);
  const first = segments[0]?.toLowerCase();
  let locale = DEFAULT_LOCALE;

  if (first && CODE_BY_SLUG.has(first)) {
    locale = CODE_BY_SLUG.get(first);
    segments.shift();
  }

  return {
    locale,
    path: segments.length ? `/${segments.join('/')}` : '/',
  };
};

export const canonicalPath = ({ locale, path }) => routeHref(path, locale);
